import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './AdminPage.module.css';

const ADMIN_KEY  = 'vakya-admin-2026';
const SESSION_KEY = 'vakya_admin_auth';
const API        = 'https://vlptp1rs69.execute-api.us-east-1.amazonaws.com';

// Sentry config — org slug and project slug visible in your Sentry URL
// Auth token — set as REACT_APP_SENTRY_TOKEN in .env or hardcode for local admin use

function apiFetch(path) {
  return fetch(`${API}${path}`, { headers: { 'X-Admin-Key': ADMIN_KEY } }).then(r => r.json());
}

// ── Shared ────────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue} style={color ? { color } : {}}>{value ?? '—'}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, loading, onRefresh }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {loading && <span className={styles.spinning}>⟳</span>}
          {onRefresh && (
            <button className={styles.refreshBtn} onClick={onRefresh} title="Refresh">
              ↻
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function ClientBadge({ type }) {
  const map = {
    'ios-app':        { label: 'iOS App',     color: '#5acea0' },
    'android-app':    { label: 'Android App', color: '#82b366' },
    'web-desktop':    { label: 'Web Desktop', color: '#7ba7d4' },
    'web-mobile':     { label: 'Web Mobile',  color: '#d4a843' },
    'web':            { label: 'Web',         color: '#7ba7d4' },
    'mobile-browser': { label: 'Mobile Web',  color: '#d4a843' },
    'desktop':        { label: 'Desktop',     color: '#7ba7d4' },
    'tablet':         { label: 'Tablet',      color: '#b08fd4' },
    'bot':            { label: 'Bot',         color: '#5A5040' },
  };
  const info = map[type] || { label: type || '—', color: '#5A5040' };
  return (
    <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.65rem', padding:'1px 7px', borderRadius:4, background: info.color+'22', border:'1px solid '+info.color+'55', color: info.color }}>
      {info.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    'ok':           { label: '✓ ok',          color: '#5acea0' },
    'error':        { label: '✕ error',        color: '#c45c5c' },
    'timeout':      { label: '⏱ timeout',      color: '#d4a843' },
    'rate_limited': { label: '⊘ rate limited', color: '#b08fd4' },
  };
  const info = map[status] || { label: status || '—', color: '#5A5040' };
  return (
    <span style={{ fontFamily:'var(--font-mono)', fontSize:'0.65rem', padding:'1px 7px', borderRadius:4, background: info.color+'22', border:'1px solid '+info.color+'55', color: info.color, whiteSpace:'nowrap' }}>
      {info.label}
    </span>
  );
}

// ── Line chart with trend ─────────────────────────────────────────────────────

function LineChart({ data, color, label }) {
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length === 0) return <div className={styles.noData}>No data</div>;
  const max = Math.max(...data.map(d => d.v), 1);
  const w = 800, h = 120, pad = 8;
  const points = data.map((d, i) => ({
    x: pad + (i / (data.length - 1 || 1)) * (w - pad * 2),
    y: pad + (1 - d.v / max) * (h - pad * 2),
    v: d.v, t: d.t,
  }));

  // Simple linear regression for trend line
  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  const trendY1 = slope * points[0].x + intercept;
  const trendY2 = slope * points[n - 1].x + intercept;

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${points[n-1].x} ${h} L ${points[0].x} ${h} Z`;


  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width:'100%', height: 140, overflow:'visible' }}
        onMouseLeave={() => setTooltip(null)}>
        <defs>
          <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <path d={areaD} fill={`url(#grad-${label})`} />
        {/* Main line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Trend line */}
        <line x1={points[0].x} y1={trendY1} x2={points[n-1].x} y2={trendY2}
          stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
        {/* Data points + hover areas */}
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => setTooltip({ x: p.x, y: p.y, t: p.t, v: p.v })}>
            <circle cx={p.x} cy={p.y} r="3" fill={color} opacity="0.8" />
            <rect x={p.x - 12} y={0} width={24} height={h} fill="transparent" />
          </g>
        ))}
        {/* Tooltip */}
        {tooltip && (
          <g>
            <line x1={tooltip.x} y1={0} x2={tooltip.x} y2={h} stroke={color} strokeWidth="1" opacity="0.3" />
            <rect x={Math.min(tooltip.x + 6, w - 90)} y={tooltip.y - 22} width={85} height={22} rx="4"
              fill="var(--surface-raised)" stroke={color} strokeWidth="1" opacity="0.95" />
            <text x={Math.min(tooltip.x + 9, w - 87)} y={tooltip.y - 7}
              fill="var(--text-primary)" fontSize="10" fontFamily="var(--font-mono)">
              {tooltip.v % 1 === 0 ? tooltip.v : tooltip.v.toFixed(0)}ms · {tooltip.t.slice(-5)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// ── OVERVIEW TAB ──────────────────────────────────────────────────────────────

function OverviewTab({ onRefresh }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/admin/stats').then(d => { setStats(d); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) return <div className={styles.loadMsg}>Loading stats…</div>;
  if (!stats) return <div className={styles.errMsg}>Failed to load</div>;

  return (
    <>
      <Section title="Platform" loading={loading} onRefresh={load}>
        <div className={styles.statGrid}>
          <StatCard label="Invocations 24h"    value={stats.invocations_24h} />
          <StatCard label="Invocations 7d"     value={stats.invocations_7d} />
          <StatCard label="Errors 24h"         value={stats.errors_24h} color={stats.errors_24h > 0 ? '#c45c5c' : undefined} />
          <StatCard label="Avg Duration"       value={stats.avg_duration_ms ? stats.avg_duration_ms + ' ms' : '—'} />
          <StatCard label="Searches Logged"    value={stats.total_searches} />
          <StatCard label="Unique Sessions"    value={stats.unique_sessions} />
          <StatCard label="Avg Search Latency" value={stats.avg_search_latency_ms != null ? stats.avg_search_latency_ms + ' ms' : '—'} />
          <StatCard label="Error Rate"         value={stats.error_rate != null ? stats.error_rate + '%' : '—'} color={stats.error_rate > 5 ? '#c45c5c' : undefined} />
          <StatCard label="Timeout Rate"       value={stats.timeout_rate != null ? stats.timeout_rate + '%' : '—'} color={stats.timeout_rate > 2 ? '#d4a843' : undefined} />
        </div>
      </Section>
      {stats.client_breakdown && Object.keys(stats.client_breakdown).length > 0 && (
        <Section title="Client Breakdown">
          <div className={styles.statGrid}>
            {Object.entries(stats.client_breakdown).map(([k,v]) => <StatCard key={k} label={k} value={v} />)}
          </div>
        </Section>
      )}
      <Section title="Top Queries">
        <table className={styles.table}>
          <thead><tr><th>#</th><th>Query</th><th>Count</th></tr></thead>
          <tbody>
            {(stats.top_queries || []).map((q,i) => (
              <tr key={i}><td className={styles.tdMono}>{i+1}</td><td>{q.query}</td><td className={styles.tdMono}>{q.count}</td></tr>
            ))}
          </tbody>
        </table>
      </Section>
    </>
  );
}

// ── SEARCHES TAB ──────────────────────────────────────────────────────────────

function SearchesTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/admin/searches').then(d => { setData(d); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className={styles.loadMsg}>Loading searches…</div>;
  if (!data) return <div className={styles.errMsg}>Failed to load</div>;

  const rows = (data.searches || []).filter(s =>
    !filter || s.query.toLowerCase().includes(filter.toLowerCase()) ||
    (s.ip||'').includes(filter) || (s.country||'').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <Section title="Recent Searches" loading={loading} onRefresh={load}>
        <div style={{ padding: '0 0 .75rem' }}>
          <input className={styles.filterInput} placeholder="Filter by query, IP, or country…"
            value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Status</th><th>Timestamp</th><th>Query</th><th>Lang</th><th>Results</th><th>Latency</th><th>IP</th><th>Country</th><th>Client</th><th>Device</th></tr>
            </thead>
            <tbody>
              {rows.map((s,i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td><StatusBadge status={s.status} /></td>
                    <td className={styles.tdMono} style={{ whiteSpace:'nowrap', fontSize:'.68rem' }}>{s.ts ? s.ts.replace('T',' ').slice(0,19) : '—'}</td>
                    <td style={{ maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={s.query}>{s.query}</td>
                    <td className={styles.tdMono}>{s.language||'—'}</td>
                    <td className={styles.tdMono}>{s.results}</td>
                    <td className={styles.tdMono}>{s.latency ? s.latency+'ms' : '—'}</td>
                    <td className={styles.tdMono}>{s.ip||'—'}</td>
                    <td className={styles.tdMono}>{s.country||'—'}</td>
                    <td><ClientBadge type={s.client_type} /></td>
                    <td className={styles.tdMono} style={{ fontSize:'.65rem' }}>{s.device_type||'—'}</td>
                  </tr>
                  {s.error_message && (
                    <tr style={{ background:'rgba(196,92,92,0.06)' }}>
                      <td colSpan={10} style={{ padding:'.3rem .75rem', fontFamily:'var(--font-mono)', fontSize:'.65rem', color:'#c45c5c' }}>
                        ↳ {s.error_type && <strong>{s.error_type}: </strong>}{s.error_message}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.tableFooter}>{rows.length} entries</div>
      </Section>
    </>
  );
}

// ── INTEL TAB ─────────────────────────────────────────────────────────────────

const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function IntelTab() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [ipFilter, setIpFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/admin/intel').then(d => { setData(d); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className={styles.loadMsg}>Loading intelligence…</div>;
  if (!data) return <div className={styles.errMsg}>Failed to load</div>;

  const heatmap = Array.from({ length:7 }, () => Array(24).fill(0));
  Object.entries(data.heatmap || {}).forEach(([key, count]) => {
    const [day, hour] = key.split('-').map(Number);
    if (day >= 0 && day < 7 && hour >= 0 && hour < 24) heatmap[day][hour] = count;
  });
  const heatMax = Math.max(1, ...heatmap.flat());

  const ips = (data.ip_list || []).filter(ip =>
    !ipFilter || ip.ip.includes(ipFilter) || (ip.country||'').toLowerCase().includes(ipFilter.toLowerCase())
  );

  return (
    <>
      <div className={styles.statGrid} style={{ marginBottom:'1.5rem' }}>
        <StatCard label="Total Searches" value={data.total_searches} />
        <StatCard label="Unique IPs"     value={data.unique_ips} />
        <StatCard label="Countries"      value={(data.country_breakdown||[]).length} />
      </div>
      <Section title="Countries" loading={loading} onRefresh={load}>
        <div className={styles.intelCountryGrid}>
          {(data.country_breakdown||[]).map((c,i) => (
            <div key={i} className={styles.intelCountryRow}>
              <span className={styles.intelFlag}>
                {c.country && c.country !== 'Unknown'
                  ? String.fromCodePoint(...[...c.country.toUpperCase()].map(ch => 0x1F1E6 - 65 + ch.charCodeAt(0)))
                  : '🌐'}
              </span>
              <span className={styles.intelCountryName}>{c.country||'Unknown'}</span>
              <div className={styles.intelBar}><div className={styles.intelBarFill} style={{ width:`${Math.round(c.count/(data.total_searches||1)*100)}%` }} /></div>
              <span className={styles.intelCount}>{c.count}</span>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Activity Heatmap (day × hour)">
        <div style={{ overflowX:'auto' }}>
          <table className={styles.heatmapTable}>
            <thead><tr><th className={styles.heatmapCorner}></th>{Array.from({length:24},(_,h)=><th key={h} className={styles.heatmapHour}>{h}</th>)}</tr></thead>
            <tbody>
              {DAY_LABELS.map((day,d) => (
                <tr key={d}><td className={styles.heatmapDay}>{day}</td>
                  {Array.from({length:24},(_,h) => {
                    const val = heatmap[d][h];
                    return <td key={h} className={styles.heatmapCell} title={`${day} ${h}:00 — ${val}`} style={{ opacity: val===0?0.08:0.15+(val/heatMax)*0.85 }} />;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.heatmapLegend}>Hour of day (UTC) · Colour intensity = search volume</div>
      </Section>
      <Section title={`IP Addresses (${data.unique_ips})`}>
        <div style={{ padding:'0 0 .75rem' }}>
          <input className={styles.filterInput} placeholder="Filter by IP or country…" value={ipFilter} onChange={e => setIpFilter(e.target.value)} />
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>IP Address</th><th>Country</th><th>Searches</th><th>Client</th><th>Device</th><th>First Seen</th><th>Last Seen</th></tr></thead>
            <tbody>
              {ips.map((ip,i) => (
                <tr key={i}>
                  <td className={styles.tdMono}>{ip.ip}</td>
                  <td className={styles.tdMono}>{ip.country||'—'}</td>
                  <td className={styles.tdMono}>{ip.searches}</td>
                  <td><ClientBadge type={ip.client_type} /></td>
                  <td className={styles.tdMono} style={{ fontSize:'.65rem' }}>{ip.device_type||'—'}</td>
                  <td className={styles.tdMono} style={{ fontSize:'.65rem' }}>{ip.first_seen?ip.first_seen.replace('T',' ').slice(0,16):'—'}</td>
                  <td className={styles.tdMono} style={{ fontSize:'.65rem' }}>{ip.last_seen?ip.last_seen.replace('T',' ').slice(0,16):'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

// ── METRICS TAB ───────────────────────────────────────────────────────────────

const TIME_RANGES = [
  { label: '6h',  hours: 6  },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '7d',  hours: 168 },
  { label: '14d', hours: 336 },
];

function MetricsTab() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [range, setRange]       = useState(48);

  const load = useCallback((h) => {
    setLoading(true);
    apiFetch(`/admin/metrics?hours=${h||range}`).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(range); }, [range]);

  return (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:'1rem', flexWrap:'wrap' }}>
        <span style={{ fontFamily:'var(--font-mono)', fontSize:'.72rem', color:'var(--text-tertiary)' }}>Time range:</span>
        {TIME_RANGES.map(r => (
          <button key={r.hours}
            onClick={() => setRange(r.hours)}
            className={range === r.hours ? styles.rangeActive : styles.rangeBtn}>
            {r.label}
          </button>
        ))}
        <button className={styles.refreshBtn} onClick={() => load(range)} style={{ marginLeft:'auto' }}>↻ Refresh</button>
      </div>

      {loading && !data ? <div className={styles.loadMsg}>Loading metrics…</div> : !data ? <div className={styles.errMsg}>Failed to load</div> : (
        <>
          <Section title={`Invocations (${TIME_RANGES.find(r=>r.hours===range)?.label||range+'h'})`}>
            <LineChart data={data.invocations} color="var(--accent-gold)" label="invocations" />
          </Section>
          <Section title={`Errors (${TIME_RANGES.find(r=>r.hours===range)?.label||range+'h'})`}>
            <LineChart data={data.errors} color="#c45c5c" label="errors" />
          </Section>
          <Section title={`Duration ms (${TIME_RANGES.find(r=>r.hours===range)?.label||range+'h'})`}>
            <LineChart data={data.duration} color="var(--accent-blue-mid)" label="duration" />
          </Section>
        </>
      )}
    </>
  );
}

// ── ERRORS TAB ────────────────────────────────────────────────────────────────

function ErrorsTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/admin/errors').then(d => { setData(d); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className={styles.loadMsg}>Loading errors…</div>;
  if (!data) return <div className={styles.errMsg}>Failed to load</div>;

  return (
    <Section title={`Errors (${data.count})`} loading={loading} onRefresh={load}>
      {data.errors.length === 0
        ? <div className={styles.noData}>No errors in last 24h ✓</div>
        : data.errors.map((e,i) => (
            <div key={i} className={styles.errorRow}>
              <div className={styles.errorTs}>{e.timestamp}</div>
              <div className={styles.errorMsg}>{e.message}</div>
            </div>
          ))
      }
    </Section>
  );
}

// ── HEALTH TAB ────────────────────────────────────────────────────────────────

function HealthPingDot({ status }) {
  const color = { ok:'#5acea0', cold:'#7ba7d4', degraded:'#d4a843', error:'#c45c5c' }[status]||'#5A5040';
  return <span title={status} style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:color, flexShrink:0 }} />;
}

function HealthTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/admin/health-pings').then(d => { setData(d); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className={styles.loadMsg}>Loading health pings…</div>;
  if (!data) return <div className={styles.errMsg}>Failed to load</div>;

  const pings = data.pings || [];

  return (
    <>
      <div className={styles.statGrid} style={{ marginBottom:'1.5rem' }}>
        <StatCard label="Total Pings"      value={data.total} />
        <StatCard label="OK"               value={data.ok}       color="#5acea0" />
        <StatCard label="Cold Starts"      value={data.cold}     color={data.cold>0?'#7ba7d4':undefined} />
        <StatCard label="Degraded"         value={data.degraded} color={data.degraded>0?'#d4a843':undefined} />
        <StatCard label="Warmup Pings"     value={data.warmups} />
        <StatCard label="Avg Latency"      value={data.avg_latency_ms!=null?data.avg_latency_ms+' ms':'—'} />
        <StatCard label="Unhealthy Streak" value={data.unhealthy_streak} color={data.unhealthy_streak>2?'#c45c5c':undefined} />
      </div>
      <Section title="Ping Timeline (most recent right)" loading={loading} onRefresh={load}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:4, padding:'.5rem 0' }}>
          {[...pings].reverse().slice(-120).map((p,i) => <HealthPingDot key={i} status={p.status} />)}
        </div>
        <div style={{ display:'flex', gap:'1.25rem', marginTop:'.5rem', flexWrap:'wrap' }}>
          {[['ok','#5acea0'],['cold','#7ba7d4'],['degraded','#d4a843'],['error','#c45c5c']].map(([s,c]) => (
            <span key={s} style={{ display:'flex', alignItems:'center', gap:5, fontFamily:'var(--font-mono)', fontSize:'.65rem', color:'var(--text-tertiary)' }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:c, display:'inline-block' }} />{s}
            </span>
          ))}
        </div>
      </Section>
      <Section title="Ping Log">
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Status</th><th>Timestamp (UTC)</th><th>Source</th><th>Corpus</th><th>Index</th><th>Model</th><th>Latency</th><th>Error</th></tr></thead>
            <tbody>
              {pings.map((p,i) => (
                <tr key={i} style={p.status!=='ok'?{background:'rgba(196,92,92,0.04)'}:{}}>
                  <td style={{ display:'flex', alignItems:'center', gap:6 }}><HealthPingDot status={p.status} /><span className={styles.tdMono}>{p.status}</span></td>
                  <td className={styles.tdMono} style={{ whiteSpace:'nowrap', fontSize:'.68rem' }}>{p.ts?p.ts.replace('T',' ').slice(0,19):'—'}</td>
                  <td className={styles.tdMono}>
                    <span style={{ padding:'1px 7px', borderRadius:4, fontSize:'.65rem', background:p.source==='warmup'?'rgba(123,167,212,0.15)':'rgba(212,168,67,0.1)', border:p.source==='warmup'?'1px solid rgba(123,167,212,0.3)':'1px solid rgba(212,168,67,0.2)', color:p.source==='warmup'?'#7ba7d4':'#d4a843' }}>{p.source}</span>
                  </td>
                  <td className={styles.tdMono}>{p.corpus_size.toLocaleString()}</td>
                  <td className={styles.tdMono} style={{ color:p.index_loaded?'#5acea0':'#c45c5c' }}>{p.index_loaded?'✓':'✗'}</td>
                  <td className={styles.tdMono} style={{ color:p.model_loaded?'#5acea0':'#c45c5c' }}>{p.model_loaded?'✓':'✗'}</td>
                  <td className={styles.tdMono} style={{ color:p.latency_ms>5000?'#d4a843':'inherit' }}>{p.latency_ms}ms</td>
                  <td className={styles.tdMono} style={{ fontSize:'.65rem', color:'#c45c5c', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.error||''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

// ── SENTRY TAB ────────────────────────────────────────────────────────────────

function SentryTab() {
  const [issues, setIssues]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [period, setPeriod]   = useState('14d');

  const load = useCallback((p) => {
    setLoading(true);
    setError(null);
    const per = p || period;
    // Route through Lambda proxy to avoid Sentry CORS restriction
    apiFetch(`/admin/sentry?period=${per}&limit=25`)
      .then(data => {
        if (data.detail) throw new Error(data.detail);
        setIssues(data.issues || []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [period]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const LEVEL_COLOR = { error: '#c45c5c', warning: '#d4a843', info: '#7ba7d4', fatal: '#8b1a1a' };

  return (
    <Section title="Sentry Issues" loading={loading} onRefresh={() => load()}>
      <div style={{ display:'flex', gap:8, marginBottom:'1rem', alignItems:'center' }}>
        {['24h','14d',''].map(p => (
          <button key={p} onClick={() => { setPeriod(p); load(p); }}
            className={period===p?styles.rangeActive:styles.rangeBtn}>{p}</button>
        ))}
        <a href={`https://pragmatic-consulting.sentry.io/issues/?project=-1&statsPeriod=${period}`}
          target="_blank" rel="noreferrer" className={styles.sentryLink}>Open Sentry ↗</a>
      </div>

      {error && <div className={styles.errMsg} style={{ padding:'1rem' }}>{error}</div>}

      {issues && issues.length === 0 && <div className={styles.noData}>No unresolved issues ✓</div>}

      {issues && issues.map((issue, i) => (
        <div key={i} className={styles.sentryIssue}>
          <div className={styles.sentryIssueTop}>
            <span className={styles.sentryLevel} style={{ background:(LEVEL_COLOR[issue.level]||'#5A5040')+'22', color:LEVEL_COLOR[issue.level]||'#5A5040', border:'1px solid '+(LEVEL_COLOR[issue.level]||'#5A5040')+'55' }}>
              {issue.level}
            </span>
            <a href={issue.permalink} target="_blank" rel="noreferrer" className={styles.sentryTitle}>{issue.title}</a>
          </div>
          <div className={styles.sentryMeta}>
            <span className={styles.tdMono}>{issue.shortId}</span>
            <span>·</span>
            <span>{issue.culprit}</span>
            <span>·</span>
            <span>{issue.count} events</span>
            <span>·</span>
            <span>Last: {issue.lastSeen ? new Date(issue.lastSeen).toLocaleString() : '—'}</span>
          </div>
        </div>
      ))}
    </Section>
  );
}

// ── ROOT COMPONENT ────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'searches', label: 'Searches' },
  { id: 'intel',    label: '🌐 Intel' },
  { id: 'metrics',  label: 'Metrics' },
  { id: 'errors',   label: 'Errors' },
  { id: 'health',   label: '❤ Health' },
  { id: 'sentry',   label: '⚡ Sentry' },
];

export default function AdminPage() {
  const [authed,   setAuthed]   = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const [password, setPassword] = useState('');
  const [pwError,  setPwError]  = useState('');

  // Tab persistence via URL hash
  const [tab, setTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return TABS.find(t => t.id === hash) ? hash : 'overview';
  });

  function switchTab(id) {
    setTab(id);
    window.location.hash = id;
  }

  // Keep hash in sync on back/forward
  useEffect(() => {
    function onHash() {
      const hash = window.location.hash.replace('#', '');
      if (TABS.find(t => t.id === hash)) setTab(hash);
    }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  function handleLogin() {
    if (password === ADMIN_KEY) { sessionStorage.setItem(SESSION_KEY,'1'); setAuthed(true); }
    else setPwError('Invalid key');
  }

  if (!authed) {
    return (
      <div className={styles.loginWrap}>
        <div className={styles.loginCard}>
          <div className={styles.loginTitle}>वाक्य Admin</div>
          <input className={styles.loginInput} type="password" placeholder="Admin key"
            value={password} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key==='Enter' && handleLogin()} />
          {pwError && <div className={styles.loginErr}>{pwError}</div>}
          <button className={styles.loginBtn} onClick={handleLogin}>Enter</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <span className={styles.topBarTitle}>वाक्य Admin</span>
        <button className={styles.logoutBtn} onClick={() => { sessionStorage.removeItem(SESSION_KEY); setAuthed(false); }}>Logout</button>
      </div>

      <div className={styles.tabs}>
        {TABS.map(t => (
          <button key={t.id} className={tab===t.id?styles.tabOn:styles.tab} onClick={() => switchTab(t.id)}>{t.label}</button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {tab === 'overview' && <OverviewTab />}
        {tab === 'searches' && <SearchesTab />}
        {tab === 'intel'    && <IntelTab />}
        {tab === 'metrics'  && <MetricsTab />}
        {tab === 'errors'   && <ErrorsTab />}
        {tab === 'health'   && <HealthTab />}
        {tab === 'sentry'   && <SentryTab />}
      </div>
    </div>
  );
}
